import type { ChangeEventHandler, VoidFunctionComponent } from 'react';
import { Children, isValidElement, cloneElement } from 'react';
import * as _ from 'lodash';
import {
  Menu as ReactMenu,
  MenuGroup as ReactMenuGroup,
  MenuItem as ReactMenuItem,
  MenuRadioGroup as ReactMenuRadioGroup,
  FocusableItem as ReactMenuFocusableItem,
} from '@szhsin/react-menu';
import type { MenuProps, MenuGroupProps, MenuItemProps, MenuRadioGroupProps } from '@szhsin/react-menu';
import styles from './Menu.module.less';

import './Menu.less';
import './ReactMenu.less';
import Flex from '../Flex';
import Icon, { icons } from '../Icon';
import { Badge, BadgeColors, BadgeSizes } from '../Badge';

export { default as StickyMenu } from './StickyMenu';

export { MenuButton } from '@szhsin/react-menu';
export { ControlledMenu } from '@szhsin/react-menu';
export { SubMenu } from '@szhsin/react-menu';
export { FocusableItem } from '@szhsin/react-menu';
export { MenuDivider } from '@szhsin/react-menu';
export { MenuHeader } from '@szhsin/react-menu';

const Menu = (props: MenuProps) => (
  <ReactMenu
    transition
    direction="right"
    setDownOverflow
    overflow="auto"
    position="auto"
    gap={8}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
);

type RadioMenuItemProps = Omit<MenuItemProps, 'type'> & { selected: boolean };

const MenuRadioGroup = (props: MenuRadioGroupProps) => {
  const childrenWithProps = Children.map(props.children, (child) => {
    if (isValidElement<RadioMenuItemProps>(child)) {
      return cloneElement<RadioMenuItemProps>(child, {
        ...child.props,
        checked: child.props.value === props.value,
      });
    }
    return child;
  });

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <ReactMenuRadioGroup {..._.omit(props, 'children')}>{childrenWithProps}</ReactMenuRadioGroup>
  );
};

// eslint-disable-next-line react/jsx-props-no-spreading
const MenuGroup = (props: MenuGroupProps) => <ReactMenuGroup takeOverflow {...props} />;

type FossaSelectableItemProps = Omit<MenuItemProps, 'type'> & {
  count?: number;
};

const CheckboxMenuItem = (props: FossaSelectableItemProps) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <ReactMenuItem {..._.omit(props, 'children')} type="checkbox">
    <Flex justify="space-between" grow={1}>
      <div>{props.children}</div>
      <Flex gap={8}>
        {props.checked && <Icon icon={icons.check} />}
        {!_.isNil(props.count) && (
          <Badge size={BadgeSizes.Micro} color={props.checked ? BadgeColors.LightGreen : BadgeColors.LightGray}>
            {props.count}
          </Badge>
        )}
      </Flex>
    </Flex>
  </ReactMenuItem>
);

const RadioMenuItem = (props: FossaSelectableItemProps) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <ReactMenuItem {..._.omit(props, 'children')} type="radio">
    <Flex justify="space-between" grow={1}>
      <div>{props.children}</div>
      <div>
        {props.checked && <Icon icon={icons.check} />}
        {!_.isNil(props.count) && (
          <Badge size={BadgeSizes.Micro} color={props.checked ? BadgeColors.LightGreen : BadgeColors.LightGray}>
            {props.count}
          </Badge>
        )}
      </div>
    </Flex>
  </ReactMenuItem>
);

// eslint-disable-next-line react/jsx-props-no-spreading
const MenuItem = (props: Omit<MenuItemProps, 'type'>) => <ReactMenuItem {...props} />;

/**
 * A search menu item that provides a search input field.
 */
const SearchMenuItem: VoidFunctionComponent<{
  placeholder: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
}> = ({ placeholder = 'Search...', onChange }) => {
  return (
    <ReactMenuFocusableItem className={styles.searchItem}>
      {({ ref }) => (
        <div className={styles.search}>
          <input ref={ref} type="text" placeholder={placeholder} onChange={onChange} className={styles.searchInput} />
          <span className={styles.searchIcon}>
            <Icon icon={icons.search} />
          </span>
        </div>
      )}
    </ReactMenuFocusableItem>
  );
};

export { Menu, MenuGroup, MenuItem, CheckboxMenuItem, RadioMenuItem, MenuRadioGroup, SearchMenuItem };
